import { forwardRef } from 'react'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/components/ui/tooltip'
import { Sparkles, Loader2 } from 'lucide-react'
import { cn } from '@/lib/utils'

export interface CharCountTextareaProps
  extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  /** Maximum character limit */
  maxLength: number
  /** Show warning when nearing limit */
  warnAt?: number
  /** Callback when enhance button is clicked */
  onEnhance?: () => void
  /** Whether enhancement is in progress */
  isEnhancing?: boolean
  /** Disable the enhance button */
  disableEnhance?: boolean
}

/**
 * Textarea with character counter and optional AI enhancement button.
 * 
 * @example
 * ```tsx
 * <CharCountTextarea
 *   value={goal}
 *   onChange={(e) => setGoal(e.target.value)}
 *   maxLength={2000}
 *   warnAt={1800}
 *   onEnhance={() => enhancePrompt()}
 *   isEnhancing={isEnhancing}
 * />
 * ```
 */
export const CharCountTextarea = forwardRef<
  HTMLTextAreaElement,
  CharCountTextareaProps
>(
  (
    {
      maxLength,
      warnAt = maxLength * 0.9,
      className,
      onEnhance,
      isEnhancing = false,
      disableEnhance = false,
      ...props
    },
    ref
  ) => {
    const currentLength = (props.value as string)?.length || 0
    const isNearLimit = currentLength >= warnAt
    const isAtLimit = currentLength >= maxLength
    const hasContent = currentLength > 0

    return (
      <div className="relative">
        <Textarea
          ref={ref}
          maxLength={maxLength}
          className={cn(
            isAtLimit && 'border-red-500 focus-visible:ring-red-500',
            // Add padding for the enhance button if onEnhance is provided
            onEnhance && 'pr-12',
            className
          )}
          {...props}
        />
        {/* Enhance button (magic wand) */}
        {onEnhance && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className={cn(
                    'absolute top-2 right-2 h-8 w-8',
                    'text-muted-foreground hover:text-foreground',
                    'transition-colors'
                  )}
                  onClick={onEnhance}
                  disabled={isEnhancing || disableEnhance || !hasContent}
                >
                  {isEnhancing ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Sparkles className="h-4 w-4" />
                  )}
                  <span className="sr-only">Enhance with AI</span>
                </Button>
              </TooltipTrigger>
              <TooltipContent side="left">
                <p>Enhance prompt with AI</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
        {/* Character counter */}
        <div
          className={cn(
            'absolute bottom-2 right-2 text-xs pointer-events-none',
            isAtLimit
              ? 'text-red-500 font-medium'
              : isNearLimit
                ? 'text-yellow-600'
                : 'text-muted-foreground'
          )}
        >
          {currentLength} / {maxLength}
        </div>
      </div>
    )
  }
)

CharCountTextarea.displayName = 'CharCountTextarea'
